/** ehr#345 - the Audit log's filters and the plain-English names of its actions. */

export interface AuditFilters {
  q: string;
  actors: string[];
  roles: string[];
  categories: string[];
  kind: '' | 'view' | 'change';
  /** YYYY-MM-DD, both days included */
  from: string;
  to: string;
}

/** The filters as GET /audit query parameters (empty ones left out). */
export function auditQueryString(f: AuditFilters): string {
  const parts: [string, string][] = [
    ['q', f.q.trim()],
    ['actors', f.actors.join(',')],
    ['roles', f.roles.join(',')],
    ['categories', f.categories.join(',')],
    ['kind', f.kind],
    ['from', f.from],
    ['to', f.to],
  ];
  return parts
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
}

/** Reads (a chart or a record opened, viewed or listed) say what was looked at. */
const LABELS: Record<string, string> = {
  // messaging#58: Messaging's entries (its own Audit window is gone; they are read here)
  'conversation.read': 'Opened a conversation',
  'conversation.create': 'Started a conversation',
  'conversation.renamed': 'Renamed a conversation',
  'conversation.member_added': 'Added someone to a conversation',
  'conversation.member_removed': 'Removed someone from a conversation',
  'conversation.left': 'Left a conversation',
  'message.send': 'Sent a message',
  'message.update': 'Edited a message',
  'message.delete': 'Deleted a message',
  'message.pinned': 'Pinned a message',
  'message.unpinned': 'Unpinned a message',
  // messaging#58 (10-21): the Audit log showed message text to this person (a count, no text)
  'message.text.read': 'Viewed message text in the Audit log',
  'practice_setup.update': 'Changed Messaging practice setup',
  'contact.demographics.read': 'Opened the chart (Profile)',
  'contact.summary.read': 'Viewed the Summary',
  'contact.notes.read': 'Viewed notes',
  'contact.progress_notes.read': 'Viewed progress notes',
  'contact.progress_note.read': 'Opened a progress note',
  'contact.form_submissions.read': 'Viewed filled forms',
  'contact.form_submission.read': 'Opened a filled form',
  'contact.forms_sent.read': 'Viewed forms sent',
  'contact.appointments.read': 'Viewed appointments',
  'contact.billing.read': 'Viewed billing',
  'contact.authorizations.read': 'Viewed authorizations',
  // ehr#565: Reporting > Authorizations (every client's authorizations)
  'report.authorizations.read': 'Viewed the Authorizations report',
  'contact.eligibility.read': 'Viewed eligibility',
  'contact.licensure.read': 'Viewed licensure',
  'contact.links.read': 'Viewed linked charts',
  'contact.treatment_plan.list': 'Viewed treatment plans',
  'contact.treatment_plan.summary': 'Viewed the treatment plan',
  'file.read': 'Opened a file',
  'submission.read': 'Opened a form submission',
  'submission.answers.read': 'Viewed form answers',
  'patient.list': 'Browsed the client list',
  'group_note_session.read': 'Opened a group note',
  // ehr#736: + New Task's unsaved row, closed without saving (a saved task is archived instead)
  'task.discard_draft': 'Discarded an unsaved new task',
};
const VERBS: Record<string, string> = {
  create: 'created',
  update: 'updated',
  delete: 'deleted',
  remove: 'removed',
  read: 'viewed',
  list: 'listed',
  send: 'sent',
  sign: 'signed',
  submit: 'submitted',
  share: 'shared',
  restore: 'restored',
  archive: 'archived',
};
const words = (s: string) => s.replace(/_/g, ' ');

/** "contact.demographics.update" -> "Contact demographics updated"; known reads by name. */
export function actionLabel(action: string): string {
  const known = LABELS[action];
  if (known) return known;
  const parts = action.split('.').filter(Boolean);
  if (!parts.length) return action;
  const last = parts[parts.length - 1]!;
  const verb = VERBS[last];
  const subject = (verb ? parts.slice(0, -1) : parts).map(words).join(' ');
  const text = verb ? `${subject} ${verb}` : subject;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The "All actions" filter's names; Messaging's three read as Messaging's. */
const CATEGORY_LABELS: Record<string, string> = {
  message: 'Messages',
  conversation: 'Conversations',
  practice_setup: 'Messaging practice setup',
};
export function categoryLabel(c: string): string {
  const known = CATEGORY_LABELS[c];
  if (known) return known;
  const t = words(c);
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** messaging#48 (reopen), #58: which conversation a Messaging entry is about (GET /audit). */
export interface MessagingDetail {
  title: string | null;
  roomId: string | null;
  eventId: string | null;
  tab: string | null;
}

const PRACTICE_TABS: Record<string, string> = {
  quiet: 'Quiet hours',
  risk: 'Risk alerts',
  reminders: 'Reminders',
  templates: 'Templates',
  locations: 'Locations',
  titles: 'Titles',
  roles: 'Roles',
  providers: 'Providers',
};

/** "Weekend coverage", "Practice setup: Quiet hours", or "A conversation" when it has no name. */
export function messagingWhere(d: MessagingDetail | null | undefined): string {
  if (!d) return '';
  if (d.tab) return `Practice setup: ${PRACTICE_TABS[d.tab] ?? words(d.tab)}`;
  return d.title ?? (d.roomId ? 'A conversation' : '');
}

/**
 * The "Open message" link: Messaging opens that conversation at that message, and only when the
 * person is already in it (it never joins from a link).
 */
export function messagingLink(d: MessagingDetail | null | undefined): string | null {
  if (!d?.roomId) return null;
  const event = d.eventId ? `&event=${encodeURIComponent(d.eventId)}` : '';
  return `https://messages.credifyfast.com/?open=room&record=${encodeURIComponent(d.roomId)}${event}`;
}
